

import React from "react";
import PropTypes from "prop-types";
import { Card, Avatar, Tooltip } from "@mui/material";
import FacebookRoundedIcon from "@mui/icons-material/FacebookRounded";
import YouTubeIcon from "@mui/icons-material/YouTube";
import InstagramIcon from "@mui/icons-material/Instagram";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RemoveIcon from "@mui/icons-material/Remove"; // Neutral icon
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import youtubeLogoDark from "assets/images/logos/youtube-logo-dark.png";

/**
 * MetricCard Component
 * @param {Object} props
 * @param {String} profilePic - Profile picture URL
 * @param {String} name - Profile name
 * @param {String} platform - Platform type (e.g., "Facebook", "YouTube", "Instagram")
 * @param {String|Number} metricLabel - Label for the metric (e.g., "Followers", "Subscribers")
 * @param {String|Number} metricValue - Current value of the metric
 * @param {String|Number} previousMetricValue - Previous value of the metric
 */
function MetricCard({ profilePic, name, platform, metricLabel, metricValue, previousMetricValue, isQualitative = false }) {

  // console.log("Incoming Metric Value : ", metricValue);
  // console.log("Incoming Previous Metric Value : ", previousMetricValue);

  const parseValue = (value) => {
    // Only remove commas and percent signs for parsing, do NOT default to 0 yet.
    const cleanedValue = String(value).replace(/,/g, '').replace(/%/g, '');
    const numericValue = parseFloat(cleanedValue);

    // Use Number() for strict check. parseFloat("70 to 74") is 70, but Number("70 to 74") is NaN.
    // We want to return the string if it's mixed text.
    const isStrictNumber = !isNaN(Number(cleanedValue)) && cleanedValue.trim() !== "";

    // Return the cleaned string or a rounded numeric value if it parsed successfully
    if (!isStrictNumber || value === null || value === undefined) {
      return value; // Return original value for display/handling
    }
    // Use parseFloat(toFixed(2)) trick for clean, math-ready number
    return parseFloat(numericValue.toFixed(2));
  };

  const parseForCalc = (value) => {
    const cleanedValue = String(value).replace(/,/g, '').replace(/%/g, '');
    return parseFloat(cleanedValue) || 0;
  }

  // --- Values for Display and Calculation ---
  const currentValueForDisplay = parseValue(metricValue); // May be a string or rounded number
  const previousValueForDisplay = parseValue(previousMetricValue);

  // Explicitly calculate rounded numeric values for CHANGE calculation (defaulting to 0 on failure)
  const currentValueRounded = parseForCalc(metricValue);
  const previousValueRounded = parseForCalc(previousMetricValue);


  /**
   * Dynamically render the platform icon.
   */
  const renderPlatformIcon = () => {
    switch (platform) {
      case "Facebook":
        return <FacebookRoundedIcon sx={{ fontSize: 32 }} />;
      case "Instagram":
        return <InstagramIcon sx={{ fontSize: 32 }} />;
      case "YouTube":
        return (
          <MDBox
            component="img"
            src={youtubeLogoDark}
            alt="YouTube Logo"
            sx={{
              width: 20,
              height: 20,
              objectFit: "contain",
              display: "inline-block",
              mt: "2px", // Optional vertical alignment tweak
            }}
          />
        );
      default:
        return null;
    }
  };

  /**
   * Calculate Metric Change
   */
  // const calculateMetricChange = () => {
  //   const currentValue = currentValueRounded; // Will be 0 if input was "Sunday"
  //   const previousValue = previousValueRounded; // Will be 0 if input was non-numeric

  //   if (!previousValue || previousValue === 0) {
  //     // If either value is non-numeric, we cannot calculate the comparison change.
  //     // We return the neutral state, but only if both inputs are numeric (or 0).
  //     // If either is NaN (e.g., from "Sunday"), we skip calculation entirely
  //     if (isNaN(currentValue) || isNaN(previousValue)) {
  //        return { change: null, isPositive: false, isNeutral: true }; 
  //     }
  //     return { change: 0, isPositive: true, isNeutral: true };
  //   }

  //   const change = ((currentValue - previousValue) / previousValue) * 100;

  //   return {
  //     change: change.toFixed(1),
  //     difference: (currentValue - previousValue).toLocaleString(),
  //     isPositive: change > 0,
  //     isNeutral: change === 0,
  //   };
  // };

  const calculateMetricChange = () => {
    const currentValue = currentValueRounded;
    const previousValue = previousValueRounded;

    if (isNaN(currentValue) || isNaN(previousValue)) {
      return { change: null, isPositive: false, isNeutral: true };
    }

    if (previousValue === 0) {
      if (currentValue === 0) {
        return { change: 0, isPositive: true, isNeutral: true };
      }
      // If moving from 0 to something, it's technically infinite growth, 
      // but showing 100% or just the change is safer than Infinity.
      return {
        change: 100,
        difference: currentValue,
        isPositive: currentValue > 0,
        isNeutral: false
      };
    }

    // Actual difference (e.g., -7 - (-2) = -5)
    const difference = currentValue - previousValue;

    // Percentage change relative to the magnitude of previous value
    // Formula: ( (Current - Previous) / |Previous| ) * 100
    const change = (difference / Math.abs(previousValue)) * 100;

    return {
      change: Math.abs(change).toFixed(1), // Display absolute percentage, arrow shows direction
      difference: difference.toLocaleString(),
      isPositive: difference > 0, // Green if difference is positive (growth/less negative)
      isNeutral: difference === 0,
    };
  };


  // Skip change calculation if the current value is not a usable number (e.g., "Sunday")
  const isValueNumeric = !isNaN(currentValueRounded);
  const { change, isPositive, isNeutral, difference } = isValueNumeric
    ? calculateMetricChange()
    : { change: null, isPositive: false, isNeutral: true }; // Force neutral/no-change state for text


  /**
   * Metric Display Logic
   */
  const renderMetricValue = () => {
    // 1. Check if the original input was non-numeric (or null/undefined)
    if (typeof currentValueForDisplay === 'string' || currentValueForDisplay === null || currentValueForDisplay === undefined) {
      // Display the original value as text
      return String(metricValue) || "N/A";
    }

    // 2. If it's a number, apply formatting logic
    return currentValueForDisplay % 1 !== 0
      ? currentValueForDisplay.toFixed(2) // Decimal numbers formatted
      : currentValueForDisplay.toFixed(0); // Whole numbers without decimals
  }


  /**
   * Tooltip Content
   */
  let tooltipContent = "";

  if (isQualitative) {
    // ✅ QUALITATIVE MODE: Show Previous Value if available
    const hasPrevious = previousValueForDisplay !== null && previousValueForDisplay !== undefined && previousValueForDisplay !== "N/A";
    const prevText = hasPrevious
      ? `Previous Value: ${previousValueForDisplay}`
      : "Previous Value: N/A";

    let statusText = "";

    // DEBUG: Check why equality is failing or succeeding unexpectedly
    // console.log("MetricCard Check:", { current: currentValueForDisplay, previous: previousValueForDisplay, match: String(currentValueForDisplay).trim() === String(previousValueForDisplay).trim() });

    // Check if values are identical (robust check)
    if (hasPrevious && String(currentValueForDisplay).trim() === String(previousValueForDisplay).trim()) {
      statusText = "\nNo change since the last period.";
    }

    tooltipContent = `${prevText}${statusText}\n\nNote: This metric is calculated by Comparedly using public data. It is not an official Facebook, YouTube or Instagram metric.`;
  } else {
    // STANDARD MODE: Show Previous + Percent Change
    tooltipContent = isNeutral
      ? `Previous Value: ${previousValueForDisplay}\nNo change since the last period.\n\nNote: This metric is calculated by Comparedly using public data. It is not an official Facebook, YouTube or Instagram metric.`
      : isPositive
        ? `Previous Value: ${previousValueForDisplay}\nIncrease: ${difference} (${change}%)\n\nNote: This metric is calculated by Comparedly using public data. It is not an official Facebook, YouTube or Instagram metric.`
        : `Previous Value: ${previousValueForDisplay}\nDecrease: ${difference} (${change}%)\n\nNote: This metric is calculated by Comparedly using public data. It is not an official Facebook, YouTube or Instagram metric.`;
  }


  return (
    <Card
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        padding: 2,
        boxShadow: 1,
        borderRadius: "12px",
        border: "1px solid #ddd",
        overflow: "hidden",
        maxWidth: 500,
        height: 80,
        transition: "all 0.3s ease-in-out",
        '&:hover': { boxShadow: 6 },
        backgroundColor: "grey.200",
      }}
    >
      {/* 🅟 Platform Icon (Top-Right Corner) */}
      <MDBox
        sx={{
          position: "absolute",
          top: 0,
          right: 4,
          color: "#282828",
          width: "20px",
          height: "20px",
          verticalAlign: "middle",
          transformOrigin: "center",
        }}
      >
        {renderPlatformIcon()}
      </MDBox>

      {/* 🟦 Profile Picture */}
      <MDBox sx={{ marginRight: 2 }}>
        <Avatar
          src={profilePic || "https://via.placeholder.com/50"}
          alt={name}
          sx={{
            width: 38,
            height: 38,
          }}
        />
      </MDBox>

      {/* 🟩 Profile Details */}
      <MDBox>
        {/* Metric Value and Label */}
        <MDBox display="flex" alignItems="flex-end" gap={0.5}>
          <MDTypography
            variant="subtitle1"
            fontWeight="bold"
            sx={{
              fontSize: "0.95rem",
              lineHeight: 1.8,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {renderMetricValue()} {/* 🎯 Uses the new function */}
          </MDTypography>
          <MDTypography
            variant="body2"
            color="text"
            sx={{
              fontSize: "0.75rem",
              lineHeight: 1.8,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {metricLabel}
          </MDTypography>
        </MDBox>

        {/* Profile Name */}
        <MDTypography
          variant="body2"
          color="text"
          sx={{
            fontSize: "0.85rem",
            lineHeight: 1.1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "180px",
          }}
        >
          {name}
        </MDTypography>
      </MDBox>

      <Tooltip
        title={<span style={{ whiteSpace: "pre-line" }}>{tooltipContent}</span>}
        placement="bottom"
        arrow
        PopperProps={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 0],
              },
            },
          ],
        }}
      >

        {/* 🟢 Metric Change (Bottom-Right Corner) */}
        <MDBox
          sx={{
            position: "absolute",
            bottom: 4,
            right: 8,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >

          {isQualitative ? (
            /* ✅ QUALITATIVE: Always show neutral dash to allow hover */
            <RemoveIcon sx={{ fontSize: 16, color: "grey" }} />
          ) : (
            /* STANDARD: Show Percentage Changes */
            isNeutral ? (
              <RemoveIcon sx={{ fontSize: 16, color: "grey" }} />
            ) : (
              <span style={{ display: "flex", alignItems: "center", gap: "0.2rem" }}>
                <span
                  style={{
                    fontSize: "0.65rem",
                    fontStyle: "italic",
                    color: "#444",
                    marginRight: "0.25rem",
                  }}
                >
                  Comparedly:
                </span>
                <span
                  style={{
                    fontSize: "0.75rem",
                    color: isPositive ? "green" : "red",
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                    fontFamily: "Arial, sans-serif",
                  }}
                >
                  {isPositive ? `+${change}%` : `${change}%`}
                </span>
                {isPositive ? (
                  <ArrowUpwardIcon sx={{ fontSize: 16, color: "green" }} />
                ) : (
                  <ArrowDownwardIcon sx={{ fontSize: 16, color: "red" }} />
                )}
              </span>
            )
          )}


        </MDBox>
      </Tooltip>
    </Card>

  );
}

MetricCard.propTypes = {
  profilePic: PropTypes.string,
  name: PropTypes.string.isRequired,
  platform: PropTypes.string.isRequired,
  metricLabel: PropTypes.string.isRequired,
  metricValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  previousMetricValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isQualitative: PropTypes.bool,
};

export default MetricCard;